import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios, { API } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from '@phosphor-icons/react';

const STEPS = ['welcome', 'age', 'intent', 'context', 'ready'];

const track = (event, step, data = {}) => {
  axios.post(`${API}/onboarding-lite/track`, { event, step, data }).catch(() => {});
};

/* ── Step 1: Welcome ── */
const WelcomeStep = ({ onContinue, onSkip }) => (
  <div className="text-center space-y-6" data-testid="onboarding-welcome">
    <img src="/kindra-logo.png" alt="Kindra" className="h-20 mx-auto object-contain" />
    <div className="space-y-3">
      <h1 className="text-2xl font-light text-[#0F172A] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Care between appointments.
      </h1>
      <p className="text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">
        Let's build a clearer picture of your health so Kindra can support you more personally over time.
      </p>
    </div>
    <div className="space-y-3 pt-4">
      <button onClick={onContinue} className="w-full bg-[#1a3a30] text-white rounded-xl py-3.5 text-sm font-medium transition-colors hover:bg-[#163029]" data-testid="onboarding-get-started">
        Get started
      </button>
      <button onClick={onSkip} className="w-full text-slate-400 text-sm py-2 hover:text-slate-600 transition-colors" data-testid="onboarding-skip-all">
        Skip for now
      </button>
    </div>
  </div>
);

/* ── Step 2: Age Range ── */
const AGE_RANGES = ['18–25', '26–35', '36–45', '46–60', '60+'];

const AgeStep = ({ value, onChange, onContinue, onSkip }) => (
  <div className="space-y-6" data-testid="onboarding-age">
    <div className="text-center space-y-2">
      <h2 className="text-xl font-light text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Which age range best fits you?</h2>
      <p className="text-xs text-slate-400">This helps tailor guidance more appropriately to you.</p>
    </div>
    <div className="space-y-2">
      {AGE_RANGES.map(r => (
        <button key={r} onClick={() => onChange(r)}
          className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
            value === r ? 'border-[#1a3a30] bg-[#1a3a30]/5 text-[#1a3a30] font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'
          }`} data-testid={`age-${r}`}>
          {r}
        </button>
      ))}
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onContinue} className="flex-1 bg-[#1a3a30] text-white rounded-xl py-3 text-sm font-medium transition-colors hover:bg-[#163029]" data-testid="onboarding-age-continue">
        Continue
      </button>
      <button onClick={onSkip} className="text-slate-400 text-sm px-4 py-3 hover:text-slate-600 transition-colors" data-testid="onboarding-age-skip">
        Skip
      </button>
    </div>
  </div>
);

/* ── Step 3: User Intent ── */
const INTENTS = [
  { id: 'symptoms', label: 'Understanding symptoms' },
  { id: 'appointments', label: 'Preparing for appointments' },
  { id: 'patterns', label: 'Tracking patterns over time' },
  { id: 'organise', label: 'Organising my health information' },
  { id: 'exploring', label: 'Just exploring for now' },
];

const IntentStep = ({ value, onChange, onContinue, onSkip }) => (
  <div className="space-y-6" data-testid="onboarding-intent">
    <div className="text-center space-y-2">
      <h2 className="text-xl font-light text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>What would you most like help with?</h2>
      <p className="text-xs text-slate-400">This helps Kindra focus on what matters most to you.</p>
    </div>
    <div className="space-y-2">
      {INTENTS.map(i => (
        <button key={i.id} onClick={() => onChange(i.id)}
          className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
            value === i.id ? 'border-[#1a3a30] bg-[#1a3a30]/5 text-[#1a3a30] font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'
          }`} data-testid={`intent-${i.id}`}>
          {i.label}
        </button>
      ))}
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onContinue} className="flex-1 bg-[#1a3a30] text-white rounded-xl py-3 text-sm font-medium transition-colors hover:bg-[#163029]" data-testid="onboarding-intent-continue">
        Continue
      </button>
      <button onClick={onSkip} className="text-slate-400 text-sm px-4 py-3 hover:text-slate-600 transition-colors" data-testid="onboarding-intent-skip">
        Skip
      </button>
    </div>
  </div>
);

/* ── Step 4: Optional Health Context ── */
const HEALTH_OPTIONS = [
  { id: 'conditions', label: 'Conditions I manage' },
  { id: 'medications', label: 'Medications I take' },
  { id: 'symptoms', label: 'Symptoms I experience' },
  { id: 'later', label: 'Prefer to add this later' },
];

const ContextStep = ({ value, onChange, onContinue, onSkip }) => {
  const toggle = (id) => {
    if (id === 'later') {
      onChange(['later']);
      return;
    }
    const next = value.filter(v => v !== 'later');
    if (next.includes(id)) onChange(next.filter(v => v !== id));
    else onChange([...next, id]);
  };

  return (
    <div className="space-y-6" data-testid="onboarding-context">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-light text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Are you currently managing anything?</h2>
        <p className="text-xs text-slate-400">Optional — you can always add more later through the app.</p>
      </div>
      <div className="space-y-2">
        {HEALTH_OPTIONS.map(o => (
          <button key={o.id} onClick={() => toggle(o.id)}
            className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all flex items-center gap-3 ${
              value.includes(o.id) ? 'border-[#1a3a30] bg-[#1a3a30]/5 text-[#1a3a30] font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`} data-testid={`context-${o.id}`}>
            <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              value.includes(o.id) ? 'border-[#1a3a30] bg-[#1a3a30]' : 'border-slate-300'
            }`}>
              {value.includes(o.id) && <Check size={12} className="text-white" weight="bold" />}
            </span>
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onContinue} className="flex-1 bg-[#1a3a30] text-white rounded-xl py-3 text-sm font-medium transition-colors hover:bg-[#163029]" data-testid="onboarding-context-continue">
          Continue
        </button>
        <button onClick={onSkip} className="text-slate-400 text-sm px-4 py-3 hover:text-slate-600 transition-colors" data-testid="onboarding-context-skip">
          Skip
        </button>
      </div>
    </div>
  );
};

/* ── Step 5: Value Reinforcement ── */
const ReadyStep = ({ data, onComplete, loading }) => {
  const intentMap = { symptoms: 'understand what your symptoms may mean', appointments: 'prepare for appointments more clearly', patterns: 'track patterns and changes over time', organise: 'organise your health information in one place', exploring: 'explore what personalised health support looks like' };
  const intentText = intentMap[data.primary_intent] || 'support you with personalised health guidance';

  return (
    <div className="space-y-6" data-testid="onboarding-ready">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
          <Check size={24} className="text-emerald-600" weight="bold" />
        </div>
        <h2 className="text-xl font-light text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>You're all set</h2>
      </div>

      <div className="bg-[#1a3a30]/5 border border-[#1a3a30]/10 rounded-xl p-4 space-y-2">
        <p className="text-sm text-[#0F172A] font-medium">Based on what you've shared, Kindra can help you:</p>
        <ul className="space-y-1.5 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">&#x2022;</span> {intentText}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">&#x2022;</span> keep track of changes over time</li>
          <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">&#x2022;</span> prepare for appointments more clearly</li>
        </ul>
      </div>

      <p className="text-xs text-slate-400 text-center leading-relaxed">
        The more context you add over time, the more helpful and personalised Kindra becomes.
      </p>

      <button onClick={onComplete} disabled={loading}
        className="w-full bg-[#1a3a30] text-white rounded-xl py-3.5 text-sm font-medium transition-colors hover:bg-[#163029] disabled:opacity-50" data-testid="onboarding-continue-to-app">
        {loading ? 'Setting up...' : 'Continue to Kindra'}
      </button>
    </div>
  );
};

/* ── Progress Dots ── */
const ProgressDots = ({ current, total }) => (
  <div className="flex items-center justify-center gap-2" data-testid="onboarding-progress">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className={`rounded-full transition-all duration-300 ${
        i === current ? 'w-6 h-2 bg-[#1a3a30]' : i < current ? 'w-2 h-2 bg-[#1a3a30]/40' : 'w-2 h-2 bg-slate-200'
      }`} />
    ))}
  </div>
);

/* ── Main Page ── */
const OnboardingLitePage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState({
    age_range: '',
    primary_intent: '',
    health_context: [],
    skipped_fields: [],
  });

  useEffect(() => {
    if (user?.onboarding_completed) navigate('/home', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    track('onboarding_started', 0);
  }, []);

  const goForward = () => {
    setDirection(1);
    track('step_completed', step, { step_name: STEPS[step] });
    setStep(s => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep(s => Math.max(0, s - 1));
  };

  const skipStep = () => {
    const stepName = STEPS[step];
    setData(d => ({ ...d, skipped_fields: [...d.skipped_fields.filter(f => f !== stepName), stepName] }));
    track('step_skipped', step, { step_name: stepName });
    setDirection(1);
    setStep(s => s + 1);
  };

  const skipAll = async () => {
    track('onboarding_skipped', 0);
    try {
      await axios.post(`${API}/onboarding-lite/skip`);
      await refreshUser();
      navigate('/welcome', { replace: true });
    } catch {
      navigate('/welcome', { replace: true });
    }
  };

  const complete = async () => {
    setCompleting(true);
    track('onboarding_completed', step, data);
    try {
      await axios.post(`${API}/onboarding-lite/complete`, data);
      await refreshUser();
      navigate('/welcome', { replace: true });
    } catch {
      navigate('/welcome', { replace: true });
    }
  };

  // Save intermediate data
  useEffect(() => {
    if (step > 1) {
      axios.post(`${API}/onboarding-lite/save`, data).catch(() => {});
    }
  }, [step, data]);

  const variants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="onboarding-lite-page">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          {step > 0 && step < STEPS.length - 1 ? (
            <button onClick={goBack} className="p-2 -ml-2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="onboarding-back">
              <ArrowLeft size={20} />
            </button>
          ) : <div className="w-9" />}
          {step > 0 && <ProgressDots current={step} total={STEPS.length} />}
          <div className="w-9" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              {step === 0 && <WelcomeStep onContinue={goForward} onSkip={skipAll} />}
              {step === 1 && <AgeStep value={data.age_range} onChange={v => setData(d => ({ ...d, age_range: v }))} onContinue={goForward} onSkip={skipStep} />}
              {step === 2 && <IntentStep value={data.primary_intent} onChange={v => setData(d => ({ ...d, primary_intent: v }))} onContinue={goForward} onSkip={skipStep} />}
              {step === 3 && <ContextStep value={data.health_context} onChange={v => setData(d => ({ ...d, health_context: v }))} onContinue={goForward} onSkip={skipStep} />}
              {step === 4 && <ReadyStep data={data} onComplete={complete} loading={completing} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OnboardingLitePage;
